import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { wardsAPI, bedsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  BedDouble,
  Edit,
  Trash2,
  Activity,
  CircleDot,
  Wrench,
} from 'lucide-react';

const BED_TYPES = [
  { value: 'general', label: 'General Ward', defaultCharge: 800 },
  { value: 'semi_private', label: 'Semi-Private', defaultCharge: 2000 },
  { value: 'private', label: 'Private Room', defaultCharge: 4000 },
  { value: 'icu', label: 'ICU', defaultCharge: 8000 },
  { value: 'isolation', label: 'Isolation', defaultCharge: 3000 },
];

const BedGrid = ({ beds, ward, onStatusChange }) => (
  <div className="flex flex-wrap gap-2">
    {beds.map(b => {
      const colors = {
        available: 'bg-emerald-100 border-emerald-300 text-emerald-700',
        occupied: 'bg-red-100 border-red-300 text-red-700',
        maintenance: 'bg-slate-200 border-slate-300 text-slate-600',
        reserved: 'bg-amber-100 border-amber-300 text-amber-700',
      };
      const isOccupied = b.status === 'occupied';
      return (
        <div key={b.id} className="relative group">
          <button
            onClick={() => onStatusChange(b, ward)}
            className={`w-16 h-14 rounded-lg border-2 flex flex-col items-center justify-center text-xs font-medium transition-all hover:shadow-md ${colors[b.status] || colors.available}`}
            data-testid={`bed-${b.bed_number}`}
          >
            <BedDouble className="h-4 w-4 mb-0.5" />
            <span className="text-[10px]">{b.bed_number.split('-')[1] || b.bed_number}</span>
          </button>
          {/* Hover tooltip for occupied beds — show patient name + UHID */}
          {isOccupied && b.patient && (
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1 z-30 hidden group-hover:block whitespace-nowrap bg-slate-900 text-white text-[10px] px-2 py-1.5 rounded-md shadow-lg" data-testid={`bed-tooltip-${b.bed_number}`}>
              <p className="font-semibold">{b.patient.name || 'Occupied'}</p>
              {(b.patient.patient_id || b.patient.uhid) && (
                <p className="opacity-70">UHID: {b.patient.uhid || b.patient.patient_id}</p>
              )}
              <p className="opacity-70">Bed {b.bed_number}</p>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export const WardsPage = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  // v35: perm-based. Billing mode dropdown is gated on `wards.billing_mode`.
  const canSetBillingMode = !!user?.is_super_admin || hasPermission('wards.billing_mode');
  const canManageWards = !!user?.is_super_admin || hasPermission('wards.create_edit');
  const [wards, setWards] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [wardBeds, setWardBeds] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editWard, setEditWard] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', floor: '', bed_type: 'general', total_beds: 10, charge_per_day: 800, billing_mode: 'daily', charge_per_hour: 100, is_day_care: false, day_care_max_hours: 8 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [wardsRes, dashRes] = await Promise.all([wardsAPI.list(), bedsAPI.dashboard()]);
      setWards(wardsRes.data || []);
      setDashboard(dashRes.data);
      // Fetch beds for each ward
      const bedsMap = {};
      for (const w of (wardsRes.data || [])) {
        const bRes = await bedsAPI.list({ ward_id: w.id });
        bedsMap[w.id] = bRes.data || [];
      }
      setWardBeds(bedsMap);
    } catch (err) {
      toast.error('Failed to load ward data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await wardsAPI.create(form);
      toast.success('Ward created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ward');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await wardsAPI.update(editWard.id, form);
      toast.success('Ward updated successfully');
      setIsEditOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update ward');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (wardId) => {
    if (!window.confirm('Delete this ward and all its beds?')) return;
    try {
      await wardsAPI.delete(wardId);
      toast.success('Ward deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete ward');
    }
  };

  const handleBedStatusChange = async (bed, ward) => {
    if (bed.status === 'occupied') {
      toast.info('Occupied bed status can only be changed via discharge');
      return;
    }
    // Vacant bed → jump straight to /admissions with this ward + bed pre-selected.
    // This mirrors the Bed Status Dashboard "click a green bed → admit" muscle memory.
    if (bed.status === 'available') {
      const params = new URLSearchParams({
        ward: ward?.id || bed.ward_id || '',
        bed: bed.id,
        bedNum: bed.bed_number || '',
        wardName: ward?.name || '',
      });
      navigate(`/admissions?${params.toString()}`);
      return;
    }
    // Maintenance → toggle back to available (admin recovery shortcut)
    const newStatus = 'available';
    try {
      await bedsAPI.updateStatus(bed.id, { status: newStatus });
      toast.success(`Bed ${bed.bed_number} set to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update bed');
    }
  };

  const openEdit = (ward) => {
    setEditWard(ward);
    setForm({
      name: ward.name,
      floor: ward.floor || '',
      bed_type: ward.bed_type,
      total_beds: ward.total_beds,
      charge_per_day: ward.charge_per_day,
      billing_mode: ward.billing_mode || 'daily',
      charge_per_hour: ward.charge_per_hour || 100,
      is_day_care: !!ward.is_day_care,
      day_care_max_hours: ward.day_care_max_hours || 8,
    });
    setIsEditOpen(true);
  };

  const resetForm = () => setForm({ name: '', floor: '', bed_type: 'general', total_beds: 10, charge_per_day: 800, billing_mode: 'daily', charge_per_hour: 100, is_day_care: false, day_care_max_hours: 8 });

  const handleBedTypeChange = (bt) => {
    const t = BED_TYPES.find(x => x.value === bt);
    setForm({ ...form, bed_type: bt, charge_per_day: t?.defaultCharge || 800 });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  const WardForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit}>
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Ward Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., General Ward A" required data-testid="ward-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Floor</Label>
            <Input value={form.floor} onChange={e => setForm({ ...form, floor: e.target.value })} placeholder="e.g., Ground Floor" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Bed Type *</Label>
            <Select value={form.bed_type} onValueChange={handleBedTypeChange}>
              <SelectTrigger data-testid="ward-bed-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BED_TYPES.map(bt => <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Total Beds *</Label>
            <Input type="number" min={1} value={form.total_beds} onChange={e => setForm({ ...form, total_beds: parseInt(e.target.value) || 1 })} required data-testid="ward-beds-input" />
          </div>
          <div className="space-y-2">
            <Label>Charge/Day (Rs.) *</Label>
            <Input type="number" min={0} value={form.charge_per_day} onChange={e => setForm({ ...form, charge_per_day: parseFloat(e.target.value) || 0 })} required data-testid="ward-charge-input" />
          </div>
        </div>
        {/* Hourly billing — only Hospital Admin / Super Admin can flip this. Others see a disabled banner. */}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3" data-testid="billing-mode-block">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Billing Mode</Label>
              <p className="text-xs text-slate-500">Charge bed by full day or by the hour.</p>
            </div>
            {!canSetBillingMode && (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">Hospital Admin / Hacker only</Badge>
            )}
          </div>
          <div className="flex gap-2">
            <label className={`flex-1 cursor-pointer rounded-md border px-3 py-2 text-sm transition ${form.billing_mode === 'daily' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-300'} ${!canSetBillingMode ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <input type="radio" className="sr-only" name="billing_mode" value="daily" disabled={!canSetBillingMode} checked={form.billing_mode === 'daily'} onChange={() => canSetBillingMode && setForm({ ...form, billing_mode: 'daily' })} data-testid="ward-billing-mode-daily" />
              Per Day
            </label>
            <label className={`flex-1 cursor-pointer rounded-md border px-3 py-2 text-sm transition ${form.billing_mode === 'hourly' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-300'} ${!canSetBillingMode ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <input type="radio" className="sr-only" name="billing_mode" value="hourly" disabled={!canSetBillingMode} checked={form.billing_mode === 'hourly'} onChange={() => canSetBillingMode && setForm({ ...form, billing_mode: 'hourly' })} data-testid="ward-billing-mode-hourly" />
              Per Hour
            </label>
          </div>
          {form.billing_mode === 'hourly' && (
            <div className="space-y-2">
              <Label>Charge / Hour (Rs.) *</Label>
              <Input type="number" min={0} value={form.charge_per_hour} disabled={!canSetBillingMode} onChange={e => setForm({ ...form, charge_per_hour: parseFloat(e.target.value) || 0 })} required data-testid="ward-charge-hour-input" />
              <p className="text-[11px] text-slate-500">Used at discharge for hourly bed billing (rounds up to the next hour).</p>
            </div>
          )}
        </div>

        {/* Day Care toggle (v37b2) */}
        <div className="mt-3 border-t border-slate-200 pt-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="cursor-pointer">Day Care Ward</Label>
              <p className="text-[11px] text-slate-500">Patients here are admitted for short stays and auto-discharged after the set duration.</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" checked={!!form.is_day_care} onChange={e => setForm({ ...form, is_day_care: e.target.checked })} data-testid="ward-day-care-toggle" />
              <span className="relative inline-block w-10 h-5 bg-slate-300 peer-checked:bg-emerald-600 rounded-full transition">
                <span className="absolute top-0.5 left-0.5 h-4 w-4 bg-white rounded-full transition peer-checked:translate-x-5" style={{ transform: form.is_day_care ? 'translateX(20px)' : 'translateX(0)' }} />
              </span>
            </label>
          </div>
          {form.is_day_care && (
            <div className="mt-2 space-y-1">
              <Label>Day Care Max Hours *</Label>
              <Input type="number" min={1} max={24} value={form.day_care_max_hours} onChange={e => setForm({ ...form, day_care_max_hours: parseInt(e.target.value, 10) || 8 })} required data-testid="ward-day-care-hours" />
              <p className="text-[11px] text-slate-500">Auto-set "expected discharge" on day-care admissions to this many hours after admission.</p>
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); setIsEditOpen(false); }}>Cancel</Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="ward-submit-btn">
          {submitting ? 'Saving...' : submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="space-y-6" data-testid="wards-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Wards & Beds
          </h1>
          <p className="mt-1 text-slate-600">Manage wards, beds, and track occupancy · <span className="text-emerald-600 font-medium">Click any green bed</span> to admit a patient</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-ward-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Ward
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Ward</DialogTitle>
              <DialogDescription>Configure a new ward with beds</DialogDescription>
            </DialogHeader>
            <WardForm onSubmit={handleCreate} submitLabel="Create Ward" />
          </DialogContent>
        </Dialog>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Beds</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{dashboard.total_beds}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50"><BedDouble className="h-6 w-6 text-blue-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Available</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">{dashboard.available}</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50"><CircleDot className="h-6 w-6 text-emerald-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Occupied</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">{dashboard.occupied}</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50"><BedDouble className="h-6 w-6 text-red-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Maintenance</p>
                  <p className="text-3xl font-bold text-slate-600 mt-1">{dashboard.maintenance}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-100"><Wrench className="h-6 w-6 text-slate-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Occupancy</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1">{dashboard.occupancy_rate}%</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50"><Activity className="h-6 w-6 text-amber-600" /></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bed Type Breakdown */}
      {dashboard?.bed_type_stats?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Bed Type Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {dashboard.bed_type_stats.map(s => (
                <div key={s.bed_type} className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-900">{s.label}</p>
                  <p className="text-sm text-slate-500 mt-1">Rs. {s.charge_per_day}/day</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-emerald-600">{s.available} free</span>
                    <span className="text-xs text-red-600">{s.occupied} used</span>
                    <span className="text-xs text-slate-500">/ {s.total}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ward Cards with Bed Grids */}
      {wards.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-lg font-medium">No wards configured yet</p>
              <p className="mt-1">Click "Add Ward" to create your first ward</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {wards.map(w => (
            <Card key={w.id} data-testid={`ward-card-${w.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{w.name}</CardTitle>
                    <div className="flex items-center gap-3 mt-1">
                      <Badge className="bg-blue-100 text-blue-700">
                        {BED_TYPES.find(x => x.value === w.bed_type)?.label || w.bed_type}
                      </Badge>
                      {w.floor && <span className="text-sm text-slate-500">{w.floor}</span>}
                      <span className="text-sm text-slate-500">Rs. {w.charge_per_day}/day</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex gap-3 text-sm">
                      <span className="text-emerald-600 font-medium">{w.available_beds} available</span>
                      <span className="text-red-600 font-medium">{w.occupied_beds} occupied</span>
                      {w.maintenance_beds > 0 && <span className="text-slate-500">{w.maintenance_beds} maintenance</span>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(w)} data-testid={`edit-ward-${w.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(w.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-ward-${w.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <BedGrid beds={wardBeds[w.id] || []} ward={w} onStatusChange={handleBedStatusChange} />
                <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-emerald-400" /> Available</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-red-400" /> Occupied</span>
                  <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-slate-300" /> Maintenance</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Ward</DialogTitle>
            <DialogDescription>Update ward configuration</DialogDescription>
          </DialogHeader>
          <WardForm onSubmit={handleEdit} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WardsPage;
